import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '../context/ThemeContext';
import { FontProvider } from '../context/FontContext';
import { LanguageProvider } from '../context/LanguageContext';
import { CurrencyProvider } from '../context/CurrencyContext';
import { DateFormatProvider } from '../context/DateFormatContext';
import { AuthProvider } from '../context/AuthContext';
import { ToastProvider } from '../components/shared/Toast';
import { ErrorBoundary } from '../components/ErrorBoundary';
import Login from '../pages/Login';
import SetInitialPassword from '../pages/SetInitialPassword';
import ForgotPassword from '../pages/ForgotPassword';
import ResetPassword from '../pages/ResetPassword';
import ChangePasswordFirstLogin from '../pages/ChangePasswordFirstLogin';
import { Dashboard } from '../pages/Dashboard';
import { OrganizationsPage } from '../pages/Organizations/OrganizationsPage';
import { OrganizationProfilePage } from '../pages/Organizations/OrganizationProfilePage';
import { LicensesPage } from '../pages/Licenses/LicensesPage';
import { CreateLicensePage } from '../pages/Licenses/CreateLicensePage';
import { LicenseDetailPage } from '../pages/Licenses/LicenseDetailPage';
import { UpgradeRequestsPage } from '../components/UpgradeRequestsPage';
import { AnalyticsPage } from '../pages/Analytics/AnalyticsPage';
import { CustomerOnboardingPage } from '../pages/Onboarding/CustomerOnboardingPage';
import { VendorOnboardingPage } from '../pages/Onboarding/VendorOnboardingPage';
import { OnboardingDataPage } from '../pages/Onboarding/OnboardingDataPage';
import { OnboardingDetailsPage } from '../pages/Onboarding/OnboardingDetailsPage';
import { UsersPage } from '../pages/Users/UsersPage';
import { UserCreatePage } from '../pages/Users/UserCreatePage';
import { SettingsPage } from '../pages/Settings/SettingsPage';
import { ActivityLogPage } from '../pages/ActivityLog/ActivityLogPage';
import { ReportsPage } from '../pages/Reports/ReportsPage';
import { NotificationsPage } from '../pages/Notifications/NotificationsPage';
import { SupportPage } from '../pages/Support/SupportPage';
import { TermsOfUsePage } from '../pages/TermsOfUse/TermsOfUsePage';
import { PrivacyPolicyPage } from '../pages/PrivacyPolicy/PrivacyPolicyPage';
import AgreementManagementPage from '../pages/Agreements/AgreementManagementPage';
import { SubscriptionPage } from '../pages/Subscription/SubscriptionPage';
import { LoginsPage } from '../pages/Logins/LoginsPage';

import { CreateEnquiryPage } from '../pages/RFQs/CreateEnquiryPage';
import { CreateRFQPage } from '../pages/RFQs/CreateRFQPage';
import { InventoryPage } from '../pages/Inventory/InventoryPage';
import { POListPage } from '../pages/PO/POListPage';
import { PODetailsPage } from '../pages/PO/PODetailsPage';
import { OffersPage } from '../pages/Offers/OffersPage';
import { CreateOfferPage } from '../pages/Offers/CreateOfferPage';
import { OfferDetailsPage } from '../pages/Offers/OfferDetailsPage';
import { OfferSaleTermsPage } from '../pages/Offers/OfferSaleTermsPage';
import { DocumentPreviewPage } from '../pages/Documents/DocumentPreviewPage';
import { ShipmentsPage } from '../pages/Shipments/ShipmentsPage';
import { ClaimRequestsPage } from '../pages/ClaimRequests/ClaimRequestsPage';
import { VendorsPage } from '../pages/Vendors/VendorsPage';
import { BrandsPage } from '../pages/Brands/BrandsPage';
import { CategoriesPage } from '../pages/Categories/CategoriesPage';
import { ModelsPage } from '../pages/Models/ModelsPage';
import { ClassificationSocietiesPage } from '../pages/Classification/ClassificationSocietiesPage';
import { ClassificationAllSurveyorsPage } from '../pages/Classification/ClassificationAllSurveyorsPage';
import { ClassificationSurveyorsPage } from '../pages/Classification/ClassificationSurveyorsPage';
import { CustomersPage } from '../pages/Customers/CustomersPage';
import ProductHierarchy from "../pages/ProductHierarchy/ProductHierarchy";
import PartFinderIntelligencePage from "../pages/ProductHierarchy/PartFinderIntelligencePage";
import { AdminOemPmsPage } from "../pages/Pms/AdminOemPmsPage";
import { RolesPage } from '../pages/Roles/RolesPage';
import { AssignRolesPage } from '../pages/Roles/AssignRolesPage';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { TemplateLayout } from '../components/template/Layout';
import { UserProfilePage } from '../pages/Users/UserProfilePage';
import { ProfilePage } from "../pages/Profile/ProfilePage.tsx"
import { RiskManagementPage } from '../pages/RiskManagement/RiskManagementPage';
import { PortManagementPage } from '../pages/PortManagement/PortManagementPage';
import { VesselDatabasePage } from '../pages/VesselDatabase/VesselDatabasePage';
import { DataManagementPage } from '../pages/DataManagement/DataManagementPage';
import { SalesPage } from '../pages/Sales/SalesPage';
import { PurchasesPage } from '../pages/Purchases/PurchasesPage';
import { CrmPage } from '../pages/Crm/CrmPage';

/* ✅ Support Ticket Pages */
import { AllTickets } from '../pages/Support/AllTickets';
import { TicketDetail } from '../pages/Support/TicketDetail';
import SupportChatPage from '../pages/Support/SupportChatPage';
import RemoteSupportPage from '../pages/Support/RemoteSupportPage';
import { EngineerRemoteSupportProvider } from '../context/EngineerRemoteSupportProvider';
import { useDashboardFavicon } from '../hooks/useDashboardFavicon';


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      // Within staleTime, remounts reuse cache (avoids duplicate portal-wide fetches)
      refetchOnMount: false,
      staleTime: 300000,
      gcTime: 600000,
    },
  },
});

function DashboardFaviconManager() {
  useDashboardFavicon();
  return null;
}

function AdminLegacyYourRfqDetailRedirect() {
  const { id } = useParams();
  return (
    <Navigate
      to={id ? `/dashboard/admin/purchases?purchasesTab=rfqs&rfqId=${id}` : '/dashboard/admin/purchases?purchasesTab=rfqs'}
      replace
    />
  );
}

function AdminRfqDetailRedirect() {
  const { id } = useParams();
  return (
    <Navigate
      to={id ? `/dashboard/admin/purchases?purchasesTab=rfqs&rfqId=${id}` : '/dashboard/admin/purchases?purchasesTab=rfqs'}
      replace
    />
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <FontProvider>
        <LanguageProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <DashboardFaviconManager />
            <AuthProvider>
              <EngineerRemoteSupportProvider>
              <CurrencyProvider>
              <DateFormatProvider>
              <ToastProvider>
                <Routes>

                  {/* Public */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/set-password" element={<SetInitialPassword />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/change-password" element={<ChangePasswordFirstLogin />} />
                  <Route path="/onboarding/customer" element={<CustomerOnboardingPage />} />
                  <Route path="/onboarding/vendor" element={<VendorOnboardingPage />} />
                  <Route path="/offer-sale-terms" element={<OfferSaleTermsPage />} />

                  {/* Protected */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <TemplateLayout />
                      </ProtectedRoute>
                    }
                  >
                    {/* Redirect */}
                    <Route index element={<Navigate to="/dashboard" replace />} />

                    {/* Dashboard */}
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="dashboard/admin" element={<Dashboard />} />
                    <Route path="dashboard/admin/sales" element={<SalesPage />} />
                    <Route path="dashboard/admin/purchases" element={<PurchasesPage />} />
                    <Route
                      path="/dashboard/admin/product-hierarchy"
                      element={<ProductHierarchy />}
                    />
                    <Route
                      path="/dashboard/admin/partfinder-intelligence"
                      element={<PartFinderIntelligencePage />}
                    />
                    <Route path="/dashboard/admin/oem-pms" element={<AdminOemPmsPage />} />




                    {/* Admin Section */}
                    <Route path="dashboard/admin/your-rfqs" element={<Navigate to="/dashboard/admin/rfqs" replace />} />
                    <Route path="dashboard/admin/your-rfqs/:id" element={<AdminLegacyYourRfqDetailRedirect />} />
                    <Route path="dashboard/admin/rfqs" element={<Navigate to="/dashboard/admin/purchases?purchasesTab=rfqs" replace />} />
                    <Route path="dashboard/admin/rfqs/drafts" element={<Navigate to="/dashboard/admin/rfqs?tab=drafts" replace />} />
                    <Route path="dashboard/admin/rfqs/new" element={<CreateRFQPage />} />
                    <Route path="dashboard/admin/documents/view" element={<DocumentPreviewPage />} />
                    <Route path="dashboard/admin/rfqs/:id" element={<AdminRfqDetailRedirect />} />
                    <Route path="dashboard/admin/create-enquiry" element={<CreateEnquiryPage />} />
                    <Route path="dashboard/admin/inventory" element={<InventoryPage />} />
                    <Route path="dashboard/admin/offers" element={<OffersPage />} />
                    <Route path="dashboard/admin/offers/new" element={<CreateOfferPage />} />
                    <Route path="dashboard/admin/offers/:id/edit" element={<CreateOfferPage />} />
                    <Route path="dashboard/admin/offers/:id" element={<OfferDetailsPage />} />
                    <Route path="dashboard/admin/pos" element={<Navigate to="/dashboard/admin/pos/received" replace />} />
                    <Route path="dashboard/admin/pos/received" element={<POListPage direction="received" />} />
                    <Route path="dashboard/admin/pos/sent" element={<POListPage direction="sent" />} />
                    <Route path="dashboard/admin/pos/:id" element={<PODetailsPage />} />
                    <Route path="dashboard/admin/shipments" element={<ShipmentsPage />} />
                    {/* Legacy Track button URL from PO list — shipment tracking by PO/order search */}
                    <Route path="dashboard/admin/track" element={<ShipmentsPage />} />
                    <Route path="dashboard/admin/claims" element={<Navigate to="/dashboard/admin/claims/received" replace />} />
                    <Route path="dashboard/admin/claims/received" element={<ClaimRequestsPage mode="received" />} />
                    <Route path="dashboard/admin/claims/created" element={<ClaimRequestsPage mode="created" />} />

                    {/* ⭐ Role Management */}
                    <Route path="dashboard/admin/roles" element={<RolesPage />} />
                    <Route path="dashboard/admin/assign-roles" element={<AssignRolesPage />} />

                    {/* Risk Management */}
                    <Route path="dashboard/admin/risks" element={<RiskManagementPage />} />

                    {/* Vendors */}
                    <Route path="dashboard/admin/vendors" element={<VendorsPage />} />
                    <Route path="dashboard/admin/brands" element={<BrandsPage />} />
                    <Route path="dashboard/admin/categories" element={<CategoriesPage />} />
                    <Route path="dashboard/admin/models" element={<ModelsPage />} />
                    <Route path="dashboard/admin/classification-societies" element={<ClassificationSocietiesPage />} />
                    <Route
                      path="dashboard/admin/classification-surveyors"
                      element={<ClassificationAllSurveyorsPage />}
                    />
                    <Route
                      path="dashboard/admin/classification-societies/:societyId/surveyors"
                      element={<ClassificationSurveyorsPage />}
                    />

                    {/* Customers */}
                    <Route path="dashboard/admin/customers" element={<CustomersPage />} />
                    <Route path="dashboard/admin/customers/support" element={<SupportPage />} />

                    {/* Legacy Routes */}
                    <Route path="organizations" element={<OrganizationsPage />} />
                    <Route path="organizations/:id" element={<OrganizationProfilePage />} />
                    <Route path="crm" element={<CrmPage />} />
                    <Route path="agreements" element={<AgreementManagementPage />} />
                    <Route path="port-management" element={<PortManagementPage />} />
                    <Route path="vessel-database" element={<VesselDatabasePage />} />
                    <Route path="data-management" element={<DataManagementPage />} />

                    <Route path="licenses" element={<LicensesPage />} />
                    <Route path="licenses/:licenseId" element={<LicenseDetailPage />} />
                    <Route path="licenses/create" element={<CreateLicensePage />} />
                    <Route path="licenses/upgrade-requests" element={<UpgradeRequestsPage />} />

                    <Route path="onboarding-data" element={<Navigate to="/organizations?tab=onboarding" replace />} />
                    <Route path="onboarding-data/:id" element={<OnboardingDetailsPage />} />
                    <Route path="analytics" element={<AnalyticsPage />} />

                    {/* Users */}
                    <Route path="users" element={<UsersPage />} />
                    <Route path="/users/:userId" element={<UserProfilePage />} />
                    <Route path="users/new" element={<UserCreatePage />} />



                    {/* Settings + Logs */}
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="activity-log" element={<ActivityLogPage />} />
                    <Route path="reports" element={<ReportsPage />} />
                    <Route path="notifications" element={<NotificationsPage />} />

                    {/* Misc */}
                    <Route path="support" element={<Navigate to="tickets" replace />} />
                    <Route path="support/tickets" element={<AllTickets />} />
                    <Route path="support/tickets/:id" element={<TicketDetail />} />
                    <Route path="support/chat" element={<SupportChatPage />} />
                    <Route path="support/remote" element={<RemoteSupportPage />} />

                    <Route path="subscription" element={<SubscriptionPage />} />
                    <Route path="logins" element={<LoginsPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="terms-of-use" element={<TermsOfUsePage />} />
                    <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
                  </Route>
                </Routes>
              </ToastProvider>
              </DateFormatProvider>
              </CurrencyProvider>
              </EngineerRemoteSupportProvider>
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
        </LanguageProvider>
        </FontProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
